/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useFaxDeviceList.ts
 🗓️ Created: 2025-12-03
 🧩 Description:
    Hook for managing Fax Devices list with data grid.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchFaxDevices } from '../api/faxDeviceApi';
import type { FaxDevice } from '../types';

interface UseFaxDeviceListProps {
  domainId?: number;
  onRefreshNeeded?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialColumns?: any[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
}

/**
 * Hook for managing fax devices list
 */
export function useFaxDeviceList(props: UseFaxDeviceListProps = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const {
    initialColumns = [],
    initialSortBy = 'name',
    initialSortOrder = 'asc',
    initialLimit = 10
  } = props;

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchFaxDevicesData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchFaxDevices(
          {
            page: params.page,
            limit: params.limit,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
          },
          params.search,
          effectiveDomainId
        );

        return {
          data: result.data as FaxDevice[],
          pagination: result.pagination,
        };
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('faxDevices:messages.loadError');
        showError(message);
        return {
          data: [] as FaxDevice[],
          pagination: {
            page: params.page,
            limit: params.limit,
            total: 0,
            totalPages: 0,
          },
        };
      }
    },
    [showError, t, effectiveDomainId]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData: fetchFaxDevicesData,
    initialColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
    moduleName: 'faxDevices',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as FaxDevice[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

